import { FC } from "react";

import { FOLLOWING_QUERY_PARAM } from "components/constants";
import { type PresentationBannerColorType } from "components/footer/PresentationBanner";
import { t } from "i18n/i18n";
import { PillButton } from "mds/components/PillButton";
import { useSectionCapabilitiesBySectionId } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  selectClassSessionBySectionId,
  selectCurrentSectionId,
  selectCurrentUserId,
} from "store/selectors";
import { useJoinAsSectionAdmin } from "utils/hooks/use-join-as-section-admin";
import { setPresentationWindow, takeOverPresenting } from "utils/presentation";
import { Link } from "utils/routing";
import { pageUrl, withQueryParams } from "utils/urls";

interface TakeOverPresentationButtonProps {
  color: PresentationBannerColorType;
  sectionId?: string;
}

export const TakeOverPresentationButton: FC<TakeOverPresentationButtonProps> = ({
  color,
  sectionId: sectionIdProp,
}) => {
  const userId = useAppSelector(selectCurrentUserId);
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const sectionId = sectionIdProp || currentSectionId;
  const classSession = useAppSelector((state) => selectClassSessionBySectionId(state, sectionId));
  const presentedPageId = classSession?.presented_page_id;
  const isPresentingUser = classSession?.presenting_user_id === userId;
  const { joinAsSectionAdminIfNew } = useJoinAsSectionAdmin();
  const { canAuthorSection } = useSectionCapabilitiesBySectionId(sectionId);

  if (!sectionId || !presentedPageId || !canAuthorSection || isPresentingUser) {
    return null;
  }

  const presentedPageUrl = withQueryParams(pageUrl(sectionId, presentedPageId), {
    [FOLLOWING_QUERY_PARAM]: "1",
  });

  const onTakeOverPresentation = async () => {
    await joinAsSectionAdminIfNew();

    setPresentationWindow();
    return takeOverPresenting(classSession?.id, userId);
  };

  return (
    <PillButton
      as={Link}
      eventName="Presentation - Take over presentation"
      kind={`banner-${color}`}
      size="s"
      to={presentedPageUrl}
      onClick={onTakeOverPresentation}
    >
      {t("main_header.take_over_presentation")}
    </PillButton>
  );
};
